import { useState } from "react";
import { Clapperboard, Film, Lightbulb, UploadCloud, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const PITCH_POINTS = [
  { icon: Film, text: "Pitch original series, shows & formats" },
  { icon: Lightbulb, text: "Submit concepts for Founder Next & more" },
  { icon: UploadCloud, text: "Share reels, decks & production material" },
];

const CONTENT_TYPES = ["Show Concept", "Web Series", "Reality Format", "Short Film", "Docuseries", "Other"];

const INPUT_CLS =
  "w-full rounded-xl border border-stone-700/70 bg-ink-900/80 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-500 transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-1 focus:ring-gold-500/40";
const LABEL_CLS = "mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400";

const EMPTY_FORM = { name: "", email: "", phone: "", content_type: "Show Concept", title: "", description: "", links: "" };

export default function ContentSubmission() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.title.trim()) {
      toast.error("Please fill in your name, email and content title.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/submissions", form);
      toast.success(data.message || "Submission received. Our curation team will review it shortly.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="submit-content" data-testid="content-submission-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="rounded-3xl border border-gold-500/25 bg-ink-900/50 p-8 sm:p-12 lg:p-16">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
            <div>
              <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.5em] text-gold-500">
                <Clapperboard className="h-4 w-4" /> Content Submission
              </p>
              <h2 className="mt-4 font-display text-4xl font-black leading-tight text-stone-50 sm:text-5xl">
                Bring Your <span className="gold-text">Story</span> to VIEWNA
              </h2>
              <p className="mt-6 max-w-md leading-relaxed text-stone-400">
                Pitch your show, series or format directly to the VIEWNA curation team. Every submission lands
                with our content team in Mumbai.
              </p>
              <ul className="mt-9 space-y-4">
                {PITCH_POINTS.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold-500/30 bg-gold-500/10 text-gold-400">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm text-stone-300">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <form data-testid="submission-form" onSubmit={submit} className="rounded-2xl border border-gold-500/20 bg-ink-950/60 p-6 sm:p-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="submission-name" className={LABEL_CLS}>Name *</label>
                  <input id="submission-name" data-testid="submission-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={INPUT_CLS} />
                </div>
                <div>
                  <label htmlFor="submission-email" className={LABEL_CLS}>Email *</label>
                  <input id="submission-email" data-testid="submission-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={INPUT_CLS} />
                </div>
                <div>
                  <label htmlFor="submission-phone" className={LABEL_CLS}>Phone</label>
                  <input id="submission-phone" data-testid="submission-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={INPUT_CLS} />
                </div>
                <div>
                  <label htmlFor="submission-type" className={LABEL_CLS}>Content Type</label>
                  <select id="submission-type" data-testid="submission-type-select" value={form.content_type} onChange={set("content_type")} className={INPUT_CLS}>
                    {CONTENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="submission-title" className={LABEL_CLS}>Title of Your Content *</label>
                  <input id="submission-title" data-testid="submission-title-input" required value={form.title} onChange={set("title")} placeholder="e.g. Mumbai Nights — a crime thriller series" className={INPUT_CLS} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="submission-description" className={LABEL_CLS}>Pitch / Synopsis</label>
                  <textarea id="submission-description" data-testid="submission-description-input" rows={4} value={form.description} onChange={set("description")} placeholder="Tell us about your concept, format and why it fits VIEWNA..." className={`${INPUT_CLS} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="submission-links" className={LABEL_CLS}>Links (reel, deck, YouTube, Instagram)</label>
                  <input id="submission-links" data-testid="submission-links-input" value={form.links} onChange={set("links")} placeholder="https://..." className={INPUT_CLS} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="submission-submit-button"
                disabled={sending}
                className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 bg-[length:200%_100%] px-9 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-500 hover:bg-right hover:shadow-[0_0_36px_rgba(212,175,55,0.4)] disabled:opacity-60 sm:w-auto"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Submitting..." : "Submit Content"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
